import type { HexColor } from './palette/colors';
import { unityPrimitiveColorTokens } from './unityTokens';

/**
 * Direct mapping from Grafana color tokens to Unity primitive token keys.
 *
 * Grafana uses semantic color names (red, orange, blue, yellow, purple, green) with prefixes
 * (super-light, light, [base], semi-dark, dark) to create tokens like "super-light-red" or "dark-blue".
 *
 * - Grafana "red" → Unity red
 * - Grafana "orange" → Unity persimmon
 * - Grafana "green" → Unity green
 * - Grafana "blue" → Unity sky-blue
 * - Grafana "yellow" → Unity orange
 * - Grafana "purple" → Unity purple
 */
export const GRAFANA_TO_UNITY_TOKEN_MAP = {
  // Grafana red → Unity red
  'super-light-red': '--color-red-200',
  'light-red': '--color-red-400',
  red: '--color-red-600',
  'semi-dark-red': '--color-red-800',
  'dark-red': '--color-red-900',

  // Grafana orange → Unity persimmon
  'super-light-orange': '--color-accent-persimmon-100',
  'light-orange': '--color-accent-persimmon-300',
  orange: '--color-accent-persimmon-500',
  'semi-dark-orange': '--color-accent-persimmon-800',
  'dark-orange': '--color-accent-persimmon-900',

  // Grafana green → Unity green
  'super-light-green': '--color-green-100',
  'light-green': '--color-green-400',
  green: '--color-green-600',
  'semi-dark-green': '--color-green-800',
  'dark-green': '--color-green-900',

  // Grafana blue → Unity sky-blue
  'super-light-blue': '--color-accent-sky-blue-100',
  'light-blue': '--color-accent-sky-blue-300',
  blue: '--color-accent-sky-blue-600',
  'semi-dark-blue': '--color-accent-sky-blue-800',
  'dark-blue': '--color-accent-sky-blue-900',

  // Grafana yellow → Unity orange
  'super-light-yellow': '--color-orange-100',
  'light-yellow': '--color-orange-500',
  yellow: '--color-orange-700',
  'semi-dark-yellow': '--color-orange-800',
  'dark-yellow': '--color-orange-900',

  // Grafana purple → Unity purple
  'super-light-purple': '--color-purple-100',
  'light-purple': '--color-purple-300',
  purple: '--color-purple-500',
  'semi-dark-purple': '--color-purple-700',
  'dark-purple': '--color-purple-900',
} as const;

/** Unity primitive token keys used for Grafana visualization imports */
export type UnityGrafanaVisualizationTokenName =
  (typeof GRAFANA_TO_UNITY_TOKEN_MAP)[keyof typeof GRAFANA_TO_UNITY_TOKEN_MAP];

/** Fallback hex color for invalid Grafana tokens */
export const UNITY_GRAFANA_FALLBACK_HEX: HexColor = unityPrimitiveColorTokens['--color-green-500'];

/** Map of Unity primitive token keys to their hex color values for Grafana imports */
const buildUnityGrafanaVisualizationTokens = (): Record<UnityGrafanaVisualizationTokenName, HexColor> => {
  const result = {} as Record<UnityGrafanaVisualizationTokenName, HexColor>;

  for (const tokenKey of Object.values(GRAFANA_TO_UNITY_TOKEN_MAP)) {
    result[tokenKey] = unityPrimitiveColorTokens[tokenKey];
  }

  return result;
};

export const unityGrafanaVisualizationTokens = buildUnityGrafanaVisualizationTokens();
